"use client";

import React, { useEffect, useRef, useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Diamond, Globe, MapPin } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface AnimatedCounterProps {
  end: number;
  suffix: string;
  label: string;
  isVisible: boolean;
}
function AnimatedCounter({
  end,
  suffix,
  label,
  isVisible
}: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (isVisible) {
      let start = 0;
      const duration = 2000;
      const increment = end / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isVisible, end]);
  return <div className="flex flex-col items-center justify-center">
      <div className="text-5xl md:text-6xl font-bold text-primary font-default mb-2 tracking-tighter drop-shadow-[0_0_10px_rgba(var(--color-primary),0.5)]">
        {count}
        {suffix}
      </div>
      <div className="text-white text-xs md:text-sm font-medium uppercase tracking-widest text-center">
        {label}
      </div>
    </div>;
}
const hubsData = [{
  name: "Silicon Valley Hub",
  desc: "Our primary software engineering and AI research center for the Americas.",
  top: "32%",
  left: "22%",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_na.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_na.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_na.png-wvc-sizes",
    alt: "North America Software Hub"
  }
}, {
  name: "Berlin Tech Center",
  desc: "European headquarters focusing on cloud infrastructure and cybersecurity.",
  top: "28%",
  left: "48%",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_eu.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_eu.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_eu.png-wvc-sizes",
    alt: "European Tech Center"
  }
}, {
  name: "Singapore Innovation Lab",
  desc: "Regional center for fintech development and high-speed data processing.",
  top: "42%",
  left: "76%",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_apac.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_apac.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_apac.png-wvc-sizes",
    alt: "APAC Innovation Lab"
  }
}, {
  name: "São Paulo Node",
  desc: "Strategic operations center for digital transformation in emerging markets.",
  top: "68%",
  left: "31%",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_sa.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_sa.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1663779/img/software_hub_sa.png-wvc-sizes",
    alt: "South America Node"
  }
}];
export default function GlobalOperations() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="252714" id="global-footprint" className="bg-transparent text-foreground py-4 px-4 md:px-8 lg:px-12 relative aspect-[2/1] bg-contain bg-center !bg-[url(https://exotic-skylark.10web.cloud/wp-content/uploads/2026/05/028-3.jpg)] !bg-[url(https://exotic-skylark.10web.cloud/wp-content/uploads/2026/05/028-4.jpg)]" ref={ref}>
      <div className="absolute inset-0 flex flex-col items-center justify-start pt-16">
        <div className="text-center mb-8 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">Global Operation</h2>
        </div>
        
        <div className="w-full max-w-6xl mx-auto mt-auto mb-8 bg-transparent p-8 rounded-lg border border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center justify-center space-y-4" data-index="0">
              <div className="p-3 rounded-full bg-primary/10 border border-primary/20">
                <Diamond className="w-8 h-8 text-white stroke-[1.5]" />
              </div>
              <AnimatedCounter end={15} suffix="+" label="years of industry experience" isVisible={isVisible} />
            </div>

            <div className="flex flex-col items-center justify-center space-y-4" data-index="1">
              <div className="p-3 rounded-full bg-primary/10 border border-primary/20">
                <Globe className="w-8 h-8 text-white stroke-[1.5]" />
              </div>
              <AnimatedCounter end={50} suffix="+" label="countries with customer coverage" isVisible={isVisible} />
            </div>

            <div className="flex flex-col items-center justify-center space-y-4" data-index="2">
              <div className="p-3 rounded-full bg-primary/10 border border-primary/20">
                <MapPin className="w-8 h-8 text-white stroke-[1.5]" />
              </div>
              <AnimatedCounter end={500} suffix="+" label="customer cases" isVisible={isVisible} />
            </div>
          </div>
        </div>
      </div>
    </section>;
}